import { useState, useMemo, useCallback } from 'react';
import { getUploadUrl } from '../utils/config';
import './AddToTimelineModal.css';

/**
 * Modal for selecting family members to add to the timeline view.
 * Supports multi-select with members sorted by degree of separation.
 * Reusable component - can be invoked from any view.
 */
function AddToTimelineModal({
  members,           // All available members
  memberDegrees,     // Map of member ID -> { degree, path }
  focalPersonId,     // For sorting by degree
  selectedIds,       // Already-added member IDs (Set)
  onAdd,             // Callback with array of member IDs to add
  onClose,           // Close modal callback
}) {
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Sort members by degree of separation, then alphabetically
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const degreeA = memberDegrees.get(a.id)?.degree ?? Infinity;
      const degreeB = memberDegrees.get(b.id)?.degree ?? Infinity;

      if (degreeA !== degreeB) {
        return degreeA - degreeB;
      }

      // Alphabetical by full name
      const nameA = `${a.first_name} ${a.last_name || ''}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [members, memberDegrees]);

  // Filter by search query
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return sortedMembers;

    const query = searchQuery.toLowerCase();
    return sortedMembers.filter(member => {
      const fullName = `${member.first_name} ${member.last_name || ''}`.toLowerCase();
      return fullName.includes(query);
    });
  }, [sortedMembers, searchQuery]);

  // Group members by degree for visual separation
  const groupedMembers = useMemo(() => {
    const groups = new Map();

    filteredMembers.forEach(member => {
      const degree = memberDegrees.get(member.id)?.degree ?? -1;
      if (!groups.has(degree)) {
        groups.set(degree, []);
      }
      groups.get(degree).push(member);
    });

    // Sort groups by degree
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === -1) return 1; // Unknown degree at end
      if (b[0] === -1) return -1;
      return a[0] - b[0];
    });
  }, [filteredMembers, memberDegrees]);

  const handleToggle = useCallback((memberId) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allSelectableIds = filteredMembers
      .filter(m => !selectedIds.has(m.id))
      .map(m => m.id);
    setCheckedIds(new Set(allSelectableIds));
  }, [filteredMembers, selectedIds]);

  const handleDeselectAll = useCallback(() => {
    setCheckedIds(new Set());
  }, []);

  const handleAdd = useCallback(() => {
    if (checkedIds.size > 0) {
      onAdd(Array.from(checkedIds));
    }
  }, [checkedIds, onAdd]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const getDegreeLabel = (degree) => {
    if (degree === -1) return 'Unknown';
    if (degree === 0) return 'Self';
    if (degree === 1) return '1st degree';
    if (degree === 2) return '2nd degree';
    if (degree === 3) return '3rd degree';
    return `${degree}th degree`;
  };

  const getInitials = (member) => {
    return `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
  };

  const getAvatarColor = (member) => {
    const hash = (member.first_name + member.last_name).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `hsl(${hash % 360}, 45%, 65%)`;
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content add-to-timeline-modal">
        <div className="modal-header">
          <h2>Add to Timeline</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="timeline-modal-search">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="timeline-modal-actions-top">
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={handleSelectAll}
          >
            Select All
          </button>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={handleDeselectAll}
          >
            Deselect All
          </button>
          <span className="selection-count">
            {checkedIds.size} selected
          </span>
        </div>

        <div className="timeline-modal-list">
          {groupedMembers.map(([degree, membersInGroup]) => (
            <div key={degree} className="degree-group">
              <div className="degree-header">
                {getDegreeLabel(degree)}
              </div>
              {membersInGroup.map(member => {
                const isAlreadyAdded = selectedIds.has(member.id);
                const isChecked = checkedIds.has(member.id);

                return (
                  <label
                    key={member.id}
                    className={`member-row ${isAlreadyAdded ? 'disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked || isAlreadyAdded}
                      disabled={isAlreadyAdded}
                      onChange={() => handleToggle(member.id)}
                    />
                    <div className="member-row-content">
                      {member.photo_url ? (
                        <img
                          src={getUploadUrl(member.photo_url)}
                          alt=""
                          className="member-row-avatar"
                        />
                      ) : (
                        <div
                          className="member-row-avatar member-row-avatar-initials"
                          style={{ backgroundColor: getAvatarColor(member) }}
                        >
                          {getInitials(member)}
                        </div>
                      )}
                      <span className="member-row-name">
                        {member.first_name} {member.last_name || ''}
                      </span>
                      {isAlreadyAdded && (
                        <span className="already-added-badge">Added</span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          ))}

          {filteredMembers.length === 0 && (
            <div className="no-results">
              No members found matching "{searchQuery}"
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={checkedIds.size === 0}
          >
            Add {checkedIds.size > 0 ? `(${checkedIds.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddToTimelineModal;
